// ══ FU-06 (Idan) — THE PICKER'S COLLAPSIBLE SECTIONS, AS PURE FUNCTIONS ═════════════════════════════════
// SmartSendPicker shows each choice (source · e-mail campaign · WhatsApp template) as a one-line summary
// «✓ <label>: <name> · שנה» once it is made, instead of its whole grid. EVERY decision of that layer is
// made here and the picker only draws the result:
//   · `sectionsInputFrom` maps what the picker reads (the selected source ROW, the campaign list, the
//     template list, the sender numbers …) to plain facts, with the children's own rules injected;
//   · `sectionsView` turns the facts into the view (which section is collapsed, where each warning goes);
//   · `pruneOpenFor` drops a «שנה» once the section shows another choice.
// No React, no redux, no dispatch (I6): facts in, a view out.
//
// The invariants, and where each is established (pickerSections.test.js runs the exhaustive parts):
//   I1  collapsed(s) ⇒ the shown choice is valid NOW — "valid" being exactly what the child grid itself
//       would show as selected (SourcePicker's ✓, CampaignPicker's ✓ on the LIVE row, a usable template with
//       a connected sender number). Re-derived on every call; any invalidation opens the section.
//   I2  the warnings about the chosen items are exactly today's — SourcePicker's identity banners and the
//       red "no recipients" caption of the selected card, and the seed refusal — and none is ever inside a
//       hidden body: while the source is collapsed the picker draws them in its summary.
//   I3  continueEnabled ≡ !continueBlocked of today: `continueBlockedOf` is today's expression, moved here
//       clause by clause (tested against a verbatim copy; the picker's mapping of its inputs is read 1:1).
//   I4  channels are never empty — by the selectors' algebra (email = ¬waOnly, waOnly ⇒ waActive), tested
//       over their state space; this layer has no channel action at all.
//   I5  every mounted section can be opened: collapsed ⇔ «שנה» offered, otherwise its body is visible.
//   I6  pure: no import of redux, of a slice, of React or of a component.
// Mounting is NOT decided here and is not changed: the picker keeps each section's own condition, and a
// collapsed body is `display:none`, never unmounted — every effect, fetch, the BUG-34 pin and the BUG-29/31/32
// resets run exactly as before. R2 (?campaignId) returns before any section is drawn and is untouched.

import { eDataSourceStatus } from '../../Models/DataSources/DataSource';

export type SectionKey = 'source' | 'email' | 'whatsapp';
export const SECTION_KEYS: SectionKey[] = ['source', 'email', 'whatsapp'];

// ── I3 · THE CONTINUE GATE, MOVED VERBATIM FROM SmartSendPicker ────────────────────────────────────────
// Same clauses, same order, same meaning as the expression that stood in the picker (see the comments
// there for why each clause exists). Only the inputs are named: `hasTemplate` is `!!waTemplateId`.
export interface ContinueGateInput {
    activeCount: number;        // activeKeys.length
    scopeClean: boolean;        // QA BUG-29 — the slice was reset for this URL
    sourceNeedsPick: boolean;   // !dataSourceId && pickedSourceId == null
    emailNeedsPick: boolean;    // channels.email && selectedId == null
    waNeedsPick: boolean;       // channels.whatsapp && !channels.email && !waTemplateId
    email: boolean;             // channels.email
    whatsapp: boolean;          // channels.whatsapp
    hasTemplate: boolean;       // !!waTemplateId
    sourceNoCell: boolean;      // the selected source has no mobile column
}

export const continueBlockedOf = (g: ContinueGateInput): boolean => g.activeCount === 0
    || !g.scopeClean
    || g.sourceNeedsPick
    || g.emailNeedsPick
    || g.waNeedsPick
    || (g.whatsapp && g.email && !g.hasTemplate)
    || (g.whatsapp && g.sourceNoCell);

// ── the facts the sections are decided from ──────────────────────────────────────────────────────────
export type Missing = 'none' | 'email' | 'cell' | 'both';
export type LoadStatus = 'idle' | 'loading' | 'succeeded' | 'failed';
export type ZeroWarning = 'zeroResolved' | 'zeroResolvedWa';

export interface SectionsInput {
    channels: { email: boolean; whatsapp: boolean };   // the lit channels (channelsFrom)
    waActive: boolean;                                   // selectWaActive — SourcePicker's `waOn`
    // Source section — mounted exactly when today's `!dataSourceId && scopeClean`.
    sourceMounted: boolean;
    // The selected source's row: id null ⇔ none chosen, or its row is not in the loaded list.
    // `ready` = Status READY; `sendable` = sourceCanSend(row, channels) (SourcePicker's ✓ needs both).
    source: { id: number | null; name: string | null; ready: boolean; sendable: boolean };
    srcMissing: Missing;                                 // missingIdentity(row, channels), 'none' without a row
    legNoCell: boolean;                                  // SourcePicker's `legNoCell` de-duplication
    zeroWarning: ZeroWarning | null;                     // the red caption on the selected card (SourcePicker)
    // E-mail section — the campaign Continue sends (`selectedId`) and its name, non-null only while the
    // LIVE row is one CampaignPicker would tick (see sectionsInputFrom).
    email: { id: number | null; name: string | null };
    seedRefused: boolean;                                // today's `seedRefusedText != null`
    // WhatsApp section.
    wa: {
        templateId: string | null;
        listStatus: LoadStatus;                          // templatesStatus
        inList: boolean;                                 // the chosen template is in the loaded list
        blocked: boolean;                                // templateBlockReason(tpl) != null
        numberCount: number;                             // connected sender numbers
        fromNumber: string | null;
        fromNumberKnown: boolean;                        // fromNumber is one of the connected numbers
        name: string | null;                             // the card name the picker shows
    };
    // «שנה»: the selection key each section showed when the operator re-opened it.
    openFor: Partial<Record<SectionKey, string>>;
    gate: ContinueGateInput;
}

export interface SectionView {
    key: SectionKey;
    mounted: boolean;
    selectionKey: string | null;      // non-null ⇔ the choice is valid now (I1)
    collapsed: boolean;
    bodyVisible: boolean;
    canExpand: boolean;               // «שנה» offered (I5)
    summaryName: string | null;
    summaryExtra: string | null;
}

export type WarningId = 'noEmail' | 'noEmailBoth' | 'noCell' | 'noCellBoth' | ZeroWarning | 'seedRefused';
// Where a warning is drawn: 'sourceBody' = by SourcePicker inside its (visible) grid, as today;
// 'sourceSummary' = by the picker, in the source summary, while the grid is collapsed;
// 'emailHeader' = by the picker above the e-mail section's collapsible body.
export type WarningHost = 'sourceBody' | 'sourceSummary' | 'emailHeader';
export interface WarningView { id: WarningId; host: WarningHost }

export interface SectionsView {
    sections: Record<SectionKey, SectionView>;
    warnings: WarningView[];
    continueEnabled: boolean;
}

// Today's warnings about the SELECTED source, exactly as SourcePicker shows them in its grid: the identity
// banners (`warnNoEmail` / `warnNoCell`, the `bothOn` key choice, the `waOn` gate, the `legNoCell`
// de-duplication) and the red caption of the selected card (`zeroResolved`). Only where SourcePicker is
// mounted. Exported so the test can hold the layer to it.
export const sourceWarningsToday = (
    i: Pick<SectionsInput, 'channels' | 'waActive' | 'sourceMounted' | 'srcMissing' | 'legNoCell' | 'zeroWarning'>,
): WarningId[] => {
    if (!i.sourceMounted) return [];
    const bothOn = i.channels.email && i.channels.whatsapp;
    const out: WarningId[] = [];
    if (i.waActive && (i.srcMissing === 'email' || i.srcMissing === 'both')) out.push(bothOn ? 'noEmailBoth' : 'noEmail');
    if (i.waActive && (i.srcMissing === 'cell' || i.srcMissing === 'both') && !i.legNoCell) out.push(bothOn ? 'noCellBoth' : 'noCell');
    if (i.zeroWarning) out.push(i.zeroWarning);
    return out;
};

const filled = (s: string | null | undefined): s is string => typeof s === 'string' && s.length > 0;

const view = (
    key: SectionKey, mounted: boolean, selectionKey: string | null, openFor: SectionsInput['openFor'],
    summaryName: string | null, summaryExtra: string | null,
): SectionView => {
    const collapsed = mounted && selectionKey != null && openFor[key] !== selectionKey;
    return {
        key,
        mounted,
        selectionKey: mounted ? selectionKey : null,
        collapsed,
        bodyVisible: mounted && !collapsed,
        canExpand: collapsed,
        summaryName: collapsed ? summaryName : null,
        summaryExtra: collapsed ? summaryExtra : null,
    };
};

export const sectionsView = (i: SectionsInput): SectionsView => {
    // Source: chosen, its row known and named, READY, sendable on a lit channel — SourcePicker's ✓.
    const sourceKey = i.sourceMounted && i.source.id != null && filled(i.source.name)
        && i.source.ready && i.source.sendable ? String(i.source.id) : null;
    // E-mail: the campaign Continue would send, named from a live row CampaignPicker would tick.
    const emailKey = i.channels.email && i.email.id != null && filled(i.email.name) ? String(i.email.id) : null;
    // WhatsApp: chosen, in the loaded list, usable, named, and with a CONNECTED sender number whenever there
    // is a choice of numbers (that Select lives in the body; a collapsed section must not hide it).
    const waValid = i.channels.whatsapp && i.wa.templateId != null && i.wa.listStatus === 'succeeded'
        && i.wa.inList && !i.wa.blocked && filled(i.wa.name)
        && (i.wa.numberCount <= 1 || (i.wa.fromNumber != null && i.wa.fromNumberKnown));
    const waKey = waValid ? `${i.wa.templateId}|${i.wa.fromNumber ?? ''}` : null;

    const sections: Record<SectionKey, SectionView> = {
        source: view('source', i.sourceMounted, sourceKey, i.openFor, i.source.name, null),
        email: view('email', i.channels.email, emailKey, i.openFor, i.email.name, null),
        whatsapp: view('whatsapp', i.channels.whatsapp, waKey, i.openFor, i.wa.name,
            i.wa.numberCount > 1 ? i.wa.fromNumber : null),
    };

    const warnings: WarningView[] = sourceWarningsToday(i).map((id) => ({
        id, host: sections.source.collapsed ? 'sourceSummary' as const : 'sourceBody' as const,
    }));
    if (sections.email.mounted && i.seedRefused) warnings.push({ id: 'seedRefused', host: 'emailHeader' });

    return { sections, warnings, continueEnabled: !continueBlockedOf(i.gate) };
};

// «שנה» holds a section open only while it still shows the choice it was re-opened on. As soon as it shows
// anything else — a new choice, no choice (a cleared chip, a channel off, a list that failed) — the entry is
// dropped, so the NEXT choice collapses it again (including a return to the earlier choice). The picker
// applies this after each render; it never changes the render it follows (the test proves that too).
export const pruneOpenFor = (
    openFor: SectionsInput['openFor'], sections: Record<SectionKey, SectionView>,
): SectionsInput['openFor'] => {
    let changed = false;
    const next: SectionsInput['openFor'] = {};
    SECTION_KEYS.forEach((k) => {
        const v = openFor[k];
        if (v == null) return;
        if (sections[k].selectionKey === v) next[k] = v; else changed = true;
    });
    return changed ? next : openFor;
};

// ── the picker's raw values → SectionsInput ──────────────────────────────────────────────────────────
// Every rule a child grid applies is INJECTED (`deps`), so this file stays pure and the picker passes the
// real ones: SourcePicker's gate and identity rule, CampaignPicker's `isSelectable`, WhatsAppTemplatePicker's
// `templateBlockReason`.
export interface PickerRaw {
    channels: { email: boolean; whatsapp: boolean };
    waActive: boolean;
    waOnly: boolean;                                     // selectWaOnlyIntent — SourcePicker's `waOnly`
    countField: string;                                  // getChannelDescriptor(selectedChannel).resolvedCountField
    sourceMounted: boolean;                              // !dataSourceId && scopeClean
    sourceRow: any | null;                               // selectSelectedSourceRow
    legNoCell: boolean;
    selectedId: number | null;                           // what Continue sends
    pendingLegName: string | null;                       // the pending-leg path (unused since 09-channel-units)
    campaignRows: any[];                                 // CampaignPicker's grid rows (newslettersParentCampaigns)
    seedRefused: boolean;
    templateId: string | null;
    templates: any[];
    templatesStatus: LoadStatus;
    numbers: any[];                                      // waAvailability.FromNumbers
    fromNumber: string | null;
    templateCardName: string | null;
    openFor: SectionsInput['openFor'];
    gate: ContinueGateInput;
}
// `ch` is whatever channel set the picker holds (`channelsFrom(...)`, which also names the channels this
// layer does not draw), handed through untouched.
export interface PickerDeps {
    sourceCanSend: (row: any, ch: any) => boolean;
    missingIdentity: (row: any, ch: any) => Missing;
    isCampaignSelectable: (row: any) => boolean;
    templateBlocked: (tpl: any) => boolean;
}

export const sectionsInputFrom = (r: PickerRaw, d: PickerDeps): SectionsInput => {
    const row = r.sourceRow;
    const sendable = !!row && d.sourceCanSend(row, r.channels);
    // SourcePicker: `count = (waOnly ? it.ResolvedRowsCell : it[descriptor.resolvedCountField]) ?? 0`,
    // `zeroResolved = sendable && count === 0`, worded for the channel that is actually sending.
    const count = row ? ((r.waOnly ? row.ResolvedRowsCell : row[r.countField]) ?? 0) : 0;
    const zeroWarning: ZeroWarning | null = sendable && count === 0 ? (r.waOnly ? 'zeroResolvedWa' : 'zeroResolved') : null;
    // CampaignPicker ticks a card only when the CURRENT row is selectable (`isSelected = selectable && id ===
    // value`) and lists only its own rows (the parent list) — so the summary is named from that live row.
    const liveRow = r.selectedId != null ? (r.campaignRows ?? []).find((c: any) => c.CampaignID === r.selectedId) ?? null : null;
    const emailName = r.pendingLegName != null
        ? r.pendingLegName
        : (liveRow && d.isCampaignSelectable(liveRow) ? (liveRow.Name || null) : null);
    const tpl = r.templateId != null ? (r.templates ?? []).find((tp: any) => tp.TemplateId === r.templateId) ?? null : null;
    return {
        channels: r.channels,
        waActive: r.waActive,
        sourceMounted: r.sourceMounted,
        source: {
            id: row ? row.DataSourceID : null,
            name: row ? (row.Name || null) : null,
            ready: !!row && row.Status === eDataSourceStatus.READY,
            sendable,
        },
        srcMissing: row ? d.missingIdentity(row, r.channels) : 'none',
        legNoCell: r.legNoCell,
        zeroWarning,
        email: { id: r.selectedId, name: emailName },
        seedRefused: r.seedRefused,
        wa: {
            templateId: r.templateId,
            listStatus: r.templatesStatus,
            inList: tpl != null,
            blocked: tpl != null && d.templateBlocked(tpl),
            numberCount: (r.numbers ?? []).length,
            fromNumber: r.fromNumber,
            fromNumberKnown: r.fromNumber != null && (r.numbers ?? []).some((n: any) => n.FromNumber === r.fromNumber),
            name: r.templateCardName || (tpl ? (tpl.FriendlyTemplateName || tpl.TemplateName || null) : null),
        },
        openFor: r.openFor,
        gate: r.gate,
    };
};
